import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { fmtDateTime, SourceTag } from './LeadForms';

/**
 * Lead task reminders (Lead Management, Nov 2026). Mounted once in Layout.
 * Every 60 s (and when leads change / the store changes) asks /api/leads/reminders for the selected store:
 *  - broadcasts the counts as a `lead-counts` window event → badge on the Leads tab (Header);
 *  - shows an in-app banner for each task whose reminder time has come (dismissed per device);
 *  - shows a "new lead" banner for Sage / website / Pop-ins leads that arrived since the last poll, so a
 *    routed lead is visible in the store within a minute (PRD success measure);
 *  - HO only: how many unrouted leads wait in the CX queue.
 * Silent on errors; nothing is shown when there is nothing to show.
 */
const DISMISS_KEY = 'lead-reminders-dismissed';
const readDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; } };
const box = (accent) => ({ background: '#fff', border: '1px solid #ece0d1', borderLeft: `4px solid ${accent}`, borderRadius: 10, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' });
const primary = { padding: '5px 10px', background: '#c88346', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const secondary = { padding: '5px 10px', background: '#fff', color: '#7a6b5d', border: '1px solid #ece0d1', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };

export default function LeadReminders() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [arrivals, setArrivals] = useState([]);
  const [cx, setCx] = useState({ count: 0, hidden: false });
  const since = useRef(Date.now());
  const storeRef = useRef(localStorage.getItem('selectedLocation'));

  const poll = useCallback(() => {
    const storeId = localStorage.getItem('selectedLocation');
    if (storeId !== storeRef.current) { storeRef.current = storeId; since.current = Date.now(); setArrivals([]); }
    if (!storeId) return;
    api.get('/leads/reminders', { params: { storeId, since: since.current } }).then(r => {
      window.dispatchEvent(new CustomEvent('lead-counts', { detail: { due: r.data.dueCount, overdue: r.data.overdueCount, newLeads: r.data.newLeads } }));
      setReminders(r.data.reminders || []);
      const fresh = r.data.inbound || [];
      if (fresh.length) {
        setArrivals(a => [...fresh.filter(x => !a.some(y => y.id === x.id)), ...a].slice(0, 3));
        window.dispatchEvent(new Event('leads-arrived'));
      }
      if (r.data.serverTime) since.current = r.data.serverTime;
      setCx(c => ({ count: Number(r.data.cxCount) || 0, hidden: c.hidden && Number(r.data.cxCount) <= c.count }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60000);
    const on = () => setTimeout(poll, 300);
    window.addEventListener('leads-changed', on);
    window.addEventListener('locationchange', on);
    return () => { clearInterval(t); window.removeEventListener('leads-changed', on); window.removeEventListener('locationchange', on); };
  }, [poll]);

  const dismiss = (id) => {
    const next = [...dismissed.filter(x => x !== id), id].slice(-200);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
  };
  const drop = (id) => setArrivals(a => a.filter(x => x.id !== id));
  const show = reminders.filter(r => !dismissed.includes(r.id)).slice(0, 3);
  const showCx = cx.count > 0 && !cx.hidden;
  if (!show.length && !arrivals.length && !showCx) return null;
  return (
    <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1500, display: 'flex', flexDirection: 'column', gap: 8, width: 'min(340px, calc(100vw - 32px))' }}>
      {showCx && (
        <div style={box('#6d28d9')}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6d28d9' }}>📥 CX queue</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3d2b1f', marginTop: 2 }}>{cx.count} unrouted lead{cx.count === 1 ? '' : 's'} waiting for a store</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={() => { try { sessionStorage.setItem('leads-store', 'CX'); } catch { /* storage blocked */ } setCx(c => ({ ...c, hidden: true })); navigate('/leads'); window.dispatchEvent(new Event('leads-store-cx')); }} style={primary}>Open CX queue</button>
            <button onClick={() => setCx(c => ({ ...c, hidden: true }))} style={secondary}>Later</button>
          </div>
        </div>
      )}
      {arrivals.map(l => (
        <div key={`n${l.id}`} style={box('#6d28d9')}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6d28d9', display: 'flex', gap: 6, alignItems: 'center' }}>📥 New lead <SourceTag source={l.source} /></div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3d2b1f', marginTop: 2 }}>{[l.first_name, l.last_name].filter(Boolean).join(' ')} · {l.lead_code}</div>
          {l.visit_day && <div style={{ fontSize: 12, color: '#7a6b5d' }}>Visit day: {new Date(l.visit_day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={() => { drop(l.id); navigate(`/leads/${l.id}`); }} style={primary}>Open lead</button>
            <button onClick={() => drop(l.id)} style={secondary}>Dismiss</button>
          </div>
        </div>
      ))}
      {show.map(r => (
        <div key={r.id} style={box('#c88346')}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#c88346' }}>⏰ Task reminder</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3d2b1f', marginTop: 2 }}>{r.subject}</div>
          <div style={{ fontSize: 12, color: '#7a6b5d' }}>{fmtDateTime(r.scheduled_at)}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={() => { dismiss(r.id); navigate(`/leads/${r.lead_id}`); }} style={primary}>Open lead</button>
            <button onClick={() => dismiss(r.id)} style={secondary}>Dismiss</button>
          </div>
        </div>
      ))}
    </div>
  );
}
